import {TimeZone} from '@hconnect/uikit'
import {PageContainer} from '@hconnect/uikit/src/lib2'
import {
  defaultQuickSelectionItems,
  timeRangeParamToDateRange
} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/dateRangePickerHelpers'
import moment from 'moment-timezone'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {JtbdCards} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {useJobsToBeDone} from '../hooks'
import {extractUniqueFrequencies, filterAndGroupByFrequency} from '../utils'

interface Props {
  plantUpmId: string
  timezone: TimeZone
  associatedRole: string
  isEditAllowed: boolean
  selectedFrequency: string
  dateRange?: string
  onFrequenciesLoaded: (frequencies: string[]) => void
}

export const JtbdDashboardContainer: React.FC<Props> = ({
  plantUpmId,
  associatedRole,
  isEditAllowed,
  timezone,
  selectedFrequency,
  dateRange,
  onFrequenciesLoaded
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const queryParams = useMemo(() => {
    const [fromDate, toDate] = timeRangeParamToDateRange({
      timeRange: dateRange,
      selectionItems: defaultQuickSelectionItems(t, timezone)
    })

    const from = fromDate ? moment(fromDate).tz(timezone) : null
    const to = toDate ? moment(toDate).tz(timezone) : null

    return {
      fromDate: from
        ? moment.utc([from.year(), from.month(), from.date()]).startOf('day').toISOString()
        : undefined,
      toDate: to
        ? moment.utc([to.year(), to.month(), to.date()]).endOf('day').toISOString()
        : undefined
    }
  }, [dateRange, t, timezone])

  const {data = [], isLoading, isError} = useJobsToBeDone(plantUpmId, associatedRole, queryParams)

  // Extract unique frequencies and notify parent
  React.useEffect(() => {
    if (data.length > 0) {
      const frequencies = extractUniqueFrequencies(data)
      onFrequenciesLoaded(frequencies)
    }
  }, [data, onFrequenciesLoaded])

  // Filter data based on selected frequency
  const filteredData = useMemo(() => {
    return filterAndGroupByFrequency(data, selectedFrequency)
  }, [data, selectedFrequency])

  if (isLoading) {
    return (
      <PageContainer>
        <div>{t('jobsToBeDone.common.loading')}</div>
      </PageContainer>
    )
  }

  if (isError) {
    return (
      <PageContainer>
        <div>{t('jobsToBeDone.common.errorData')}</div>
      </PageContainer>
    )
  }

  return (
    <JtbdCards
      plantUpmId={plantUpmId}
      items={filteredData}
      timezone={timezone}
      isEditAllowed={isEditAllowed}
      associatedRole={associatedRole}
      insightQueryParams={queryParams}
    />
  )
}
